import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';

interface AttractionThemeDropdownProps {
  attractionId?: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  themes?: AttractionTheme[]; // Pass themes directly to avoid fetching
}

interface AttractionTheme {
  id: string;
  name?: string | null;
  themeType?: {
    id: string;
    name: string;
    slug: string;
  };
}

export function AttractionThemeDropdown({
  attractionId,
  value,
  onChange,
  placeholder = 'Select Theme',
  className,
  disabled,
  themes: themesFromProps,
}: AttractionThemeDropdownProps) {
  const apiClient = useApiClient();

  // Fetch themes for this attraction only if not provided via props
  const { data: themesData, isLoading } = useQuery({
    queryKey: ['AttractionTheme', 'list', attractionId],
    queryFn: async () => {
      if (!attractionId) return { data: [], total: 0 };
      const response = await apiClient.get<{ data: AttractionTheme[]; total: number }>(
        `/attractions/${attractionId}/themes`
      );
      return response;
    },
    enabled: !!attractionId && !themesFromProps,
  });

  const themes = themesFromProps || themesData?.data || [];

  const handleValueChange = (newValue: string) => {
    if (newValue === '__tour_default__') {
      onChange?.(null);
    } else {
      onChange?.(newValue);
    }
  };

  return (
    <Select
      value={value === null || value === undefined ? '__tour_default__' : value}
      onValueChange={handleValueChange}
      disabled={disabled || (isLoading && !themesFromProps) || !attractionId}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {/* Tour default option */}
        <SelectItem value="__tour_default__">
          <span className="text-muted-foreground">Tour default</span>
        </SelectItem>

        {/* Available themes */}
        {themes.map((theme) => (
          <SelectItem key={theme.id} value={theme.id}>
            {theme.themeType?.name || theme.name || 'Unnamed Theme'}
          </SelectItem>
        ))}

        {/* No themes available message */}
        {themes.length === 0 && !isLoading && !themesFromProps && (
          <div className="py-2 px-2 text-sm text-muted-foreground">
            No themes available for this attraction
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
